import { useMemo } from 'react';
import { useNodeRender } from '@flowgram.ai/free-layout-editor';
import type { FlowNodeData, ModelConfig } from '$/domains/workFlowV3/type';
import useFlowNextStore from '$/store/useTeamFlowStore';
import { TVFlowNodeNextTypeEnum } from '../config/node.config';
import {
  getCurrentToolParameters,
  getNodeGenerationConfig,
} from '../utils/modelConfig';

export interface MediaSafeAreaSize {
  width: number;
  height: number;
}

export interface MediaSafeAreaResource {
  url?: string;
  originUrl?: string;
  watermark_url?: string;
  width?: number | string;
  height?: number | string;
  ratio?: number | string;
}

export interface MediaSafeAreaPlaceholderSize {
  width?: number;
  height?: number;
}

export interface MediaSafeAreaParams extends MediaSafeAreaResource {
  /** 宽高比，由外部业务明确传入，支持 1、1.777、"16:9"、"16/9" 等格式。 */
  ratio?: number | string;
  /** 媒体资源，有真实资源且有宽高时优先使用资源自身比例。 */
  media?: MediaSafeAreaResource;
}

export type MediaSafeAreaNodeData = Partial<FlowNodeData<MediaSafeAreaParams>> & {
  placeholderSize?: MediaSafeAreaPlaceholderSize;
};

interface MediaSafeAreaModelParam {
  key: string;
  value?: string | number | boolean;
}

interface GetModelParamByTypeParams {
  type: 'ratio';
  nodeType: TVFlowNodeNextTypeEnum;
  nodeData?: MediaSafeAreaNodeData;
}

interface MediaSafeAreaStoreState {
  modelConfig: ModelConfig;
}

const MEDIA_SAFE_AREA_FIXED_SIZE = 240;
const RATIO_EPSILON = 0.000001;

/**
 * 解析媒体资源可用地址，无 url 时回退到水印图。
 */
function resolveMediaResourceUrl(media?: MediaSafeAreaResource): string {
  const url = media?.originUrl || media?.url || media?.watermark_url;
  return typeof url === 'string' ? url.trim() : '';
}

/**
 * 判断媒体资源是否已有真实地址与有效宽高。
 */
function hasValidMediaResourceSize(media?: MediaSafeAreaResource): boolean {
  const width = Number(media?.width);
  const height = Number(media?.height);

  return Boolean(
    media
    && resolveMediaResourceUrl(media)
    && Number.isFinite(width)
    && Number.isFinite(height)
    && width > 0
    && height > 0,
  );
}

/**
 * 判断媒体是否已有真实地址，避免已落图节点继续用生成比例参数改动展示尺寸。
 */
function hasMediaRealUrl(media?: MediaSafeAreaResource): boolean {
  return resolveMediaResourceUrl(media).length > 0;
}

/**
 * 规范化模型执行中的占位尺寸，仅接受正数宽高。
 */
function normalizePlaceholderSize(size?: MediaSafeAreaPlaceholderSize): MediaSafeAreaSize | undefined {
  const width = Number(size?.width);
  const height = Number(size?.height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return undefined;
  }

  return {
    width,
    height,
  };
}

/**
 * 从 Flowgram 节点上下文中读取最新节点数据，供业务 hook 同步模型参数与媒体尺寸。
 */
function getNodeData(data: unknown): MediaSafeAreaNodeData {
  if (data && typeof data === 'object') {
    return data as MediaSafeAreaNodeData;
  }
  return {};
}

/**
 * 将模型参数值限制为当前业务对外返回的基础类型。
 */
function normalizeModelParamValue(value: unknown): MediaSafeAreaModelParam['value'] {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return undefined;
}

/**
 * 先从 modelConfig 当前工具中按参数 type 匹配真实 key，再从节点 mcpInfo.parameters 读取 value。
 */
function getModelParamByType({
  type,
  nodeType,
  nodeData = {},
}: GetModelParamByTypeParams): MediaSafeAreaModelParam | undefined {
  if (!type) return undefined;

  const generationConfig = getNodeGenerationConfig(nodeType, nodeData as FlowNodeData);
  const parameters = getCurrentToolParameters(nodeType, generationConfig);
  const matchedEntry = Object.entries(parameters)
    .find(([, parameter]) => parameter?.type === type);

  if (matchedEntry) {
    const [key] = matchedEntry;
    return {
      key,
      value: normalizeModelParamValue(nodeData.mcpInfo?.parameters?.[key]),
    };
  }

  return undefined;
}

/**
 * 按 240 基准边计算媒体安全区域渲染尺寸。
 */
export function getMediaSafeAreaSize(params: MediaSafeAreaParams = {}): MediaSafeAreaSize {
  const media = params.media ?? params;
  const safeAreaRatio = hasValidMediaResourceSize(media)
    ? Number(media.width) / Number(media.height)
    : params.ratio;
  let ratio = typeof safeAreaRatio === 'number' ? safeAreaRatio : Number(safeAreaRatio);

  if (typeof safeAreaRatio === 'string' && !Number.isFinite(ratio)) {
    const separator = safeAreaRatio.includes(':') ? ':' : '/';
    const [ratioWidth, ratioHeight] = safeAreaRatio.trim().split(separator).map(Number);
    ratio = ratioWidth / ratioHeight;
  }

  if (!Number.isFinite(ratio) || ratio <= 0 || Math.abs(ratio - 1) < RATIO_EPSILON) {
    return {
      width: MEDIA_SAFE_AREA_FIXED_SIZE,
      height: MEDIA_SAFE_AREA_FIXED_SIZE,
    };
  }

  if (ratio < 1) {
    return {
      width: MEDIA_SAFE_AREA_FIXED_SIZE,
      height: Math.round(MEDIA_SAFE_AREA_FIXED_SIZE / ratio),
    };
  }

  return {
    width: Math.round(MEDIA_SAFE_AREA_FIXED_SIZE * ratio),
    height: MEDIA_SAFE_AREA_FIXED_SIZE,
  };
}

/**
 * 根据节点数据与模型配置计算媒体安全区域宽高（与 useMediaSafeArea 规则一致）。
 */
export function getNodeMediaSafeAreaSize(
  nodeType: TVFlowNodeNextTypeEnum,
  nodeData: MediaSafeAreaNodeData = {},
): MediaSafeAreaSize {
  const media = nodeData.media_list?.[0];
  const placeholderSize = normalizePlaceholderSize(nodeData.placeholderSize);
  const safeAreaParam = getModelParamByType({
    type: 'ratio',
    nodeType,
    nodeData,
  });
  const ratio = typeof safeAreaParam?.value === 'number' || typeof safeAreaParam?.value === 'string'
    ? safeAreaParam.value
    : undefined;
  const mediaRatio = !hasMediaRealUrl(media)
    && (typeof media?.ratio === 'number' || typeof media?.ratio === 'string')
    ? media.ratio
    : undefined;
  const preferredRatio = mediaRatio ?? ratio;

  if (hasValidMediaResourceSize(media)) {
    return getMediaSafeAreaSize({ media });
  }
  if (!hasMediaRealUrl(media) && placeholderSize) {
    return placeholderSize;
  }
  if (hasMediaRealUrl(media) || !preferredRatio) {
    return getMediaSafeAreaSize();
  }

  return getMediaSafeAreaSize({ ratio: preferredRatio });
}

/**
 * 根据媒体资源与 ratio 模型参数返回媒体安全区域宽高。
 */
export default function useMediaSafeArea(): MediaSafeAreaSize {
  const { node, data } = useNodeRender();
  const modelConfig = useFlowNextStore((state: MediaSafeAreaStoreState) => state.modelConfig);
  const nodeType = node.flowNodeType as TVFlowNodeNextTypeEnum;
  const nodeData = getNodeData(data ?? node.toJSON()?.data);

  return useMemo(
    () => getNodeMediaSafeAreaSize(nodeType, nodeData),
    [modelConfig, nodeData, nodeType],
  );
}
